import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DescriptionIcon from '@mui/icons-material/Description';
import { createApiClient } from '../../api/client';
import { useAuth } from '../../auth/useAuth';

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  bookId: string;
  bookTitle: string;
}

type ExportFormat = 'epub' | 'pdf' | 'json' | 'markdown';

interface FormatOption {
  format: ExportFormat;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const FORMAT_OPTIONS: FormatOption[] = [
  {
    format: 'epub',
    label: 'EPUB3',
    description: 'Full ebook with images and audio',
    icon: <MenuBookIcon sx={{ fontSize: 40 }} />,
  },
  {
    format: 'pdf',
    label: 'PDF',
    description: 'Print-ready with images',
    icon: <PictureAsPdfIcon sx={{ fontSize: 40 }} />,
  },
  {
    format: 'json',
    label: 'JSON',
    description: 'Structured data export',
    icon: <DataObjectIcon sx={{ fontSize: 40 }} />,
  },
  {
    format: 'markdown',
    label: 'Markdown',
    description: 'Chapter files with YAML front matter',
    icon: <DescriptionIcon sx={{ fontSize: 40 }} />,
  },
];

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

interface ExportResult {
  exportId?: string;
  downloadUrl?: string;
  status?: string;
}

/**
 * Export dialog presenting format cards. Clicking a format triggers
 * a backend export. For EPUB the endpoint exists; for other formats
 * the endpoint may not exist yet, so we show a "queued" toast fallback.
 */
export function ExportDialog({
  open,
  onClose,
  bookId,
  bookTitle,
}: ExportDialogProps) {
  const { getToken } = useAuth();
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(
    null,
  );
  const [isExporting, setIsExporting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async (format: ExportFormat) => {
    setSelectedFormat(format);
    setIsExporting(true);
    setDownloadUrl(null);
    setExportMessage(null);
    setError(null);

    try {
      const client = createApiClient(getToken);

      // Each export format has its own dedicated endpoint.
      const url = `api/v2/books/${bookId}/export/${format}`;

      const response = await client
        .post(url, { timeout: 120_000 })
        .json<ApiEnvelope<ExportResult>>();

      if (response.data?.downloadUrl) {
        setDownloadUrl(response.data.downloadUrl);
      } else {
        setExportMessage(
          "Export queued -- you'll receive a notification when it's ready",
        );
      }
    } catch {
      // Backend endpoint may not exist yet for non-EPUB formats
      setExportMessage(
        "Export queued -- you'll receive a notification when it's ready",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFormat(null);
    setIsExporting(false);
    setDownloadUrl(null);
    setExportMessage(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Export "{bookTitle}"</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* Format cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            {FORMAT_OPTIONS.map((opt) => (
              <Paper
                key={opt.format}
                variant="outlined"
                onClick={() => !isExporting && handleExport(opt.format)}
                sx={{
                  p: 2,
                  cursor: isExporting ? 'default' : 'pointer',
                  textAlign: 'center',
                  border: 2,
                  borderColor:
                    selectedFormat === opt.format ? 'primary.main' : 'divider',
                  opacity:
                    isExporting && selectedFormat !== opt.format ? 0.5 : 1,
                  '&:hover': isExporting
                    ? {}
                    : {
                        borderColor: 'primary.light',
                        bgcolor: 'action.hover',
                      },
                  transition: 'all 0.15s',
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 1 }}>{opt.icon}</Box>
                <Typography variant="subtitle2">{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.description}
                </Typography>
              </Paper>
            ))}
          </Box>

          {/* Exporting spinner */}
          {isExporting && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">
                Exporting as {selectedFormat?.toUpperCase()}...
              </Typography>
            </Stack>
          )}

          {/* Download link */}
          {downloadUrl && (
            <Alert severity="success">
              <Typography variant="body2">
                Export ready.{' '}
                <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                  Download {selectedFormat?.toUpperCase()}
                </a>
              </Typography>
            </Alert>
          )}

          {/* Queued message */}
          {exportMessage && !downloadUrl && (
            <Alert severity="info">
              <Typography variant="body2">{exportMessage}</Typography>
            </Alert>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error">
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
